import * as Tone from 'tone';
import { getSoundfontBus } from './soundfontInstruments';

// Hard safety ceiling: no recording can run longer than this, regardless of
// app state (guards against anything that would keep playback going forever)
export const MAX_RECORDING_SECONDS = 20 * 60;

// AudioWorklet processor that batches captured samples and posts them to the
// main thread. Tone's context is a standardized-audio-context wrapper without
// createScriptProcessor, so a worklet is the supported way to tap PCM.
const WORKLET_NAME = 'mp3-capture-processor';
const WORKLET_CODE = `
class Mp3CaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._left = [];
    this._right = [];
    this._count = 0;
  }
  process(inputs) {
    const input = inputs[0];
    if (input && input.length > 0 && input[0].length > 0) {
      const left = input[0];
      const right = input.length > 1 ? input[1] : input[0];
      this._left.push(new Float32Array(left));
      this._right.push(new Float32Array(right));
      this._count += left.length;
      // Batch ~4096 samples per message to keep postMessage overhead low
      if (this._count >= 4096) {
        this.port.postMessage({ left: this._left, right: this._right });
        this._left = [];
        this._right = [];
        this._count = 0;
      }
    }
    return true;
  }
}
registerProcessor('${WORKLET_NAME}', Mp3CaptureProcessor);
`;

let workletModuleContext = null;

async function ensureWorkletModule() {
  const context = Tone.getContext();
  if (workletModuleContext === context) return;
  const url = URL.createObjectURL(new Blob([WORKLET_CODE], { type: 'application/javascript' }));
  try {
    await context.addAudioWorkletModule(url, WORKLET_NAME);
    workletModuleContext = context;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Captures the master audio output (Tone.js synths + soundfont bus) as raw
 * PCM. Call stop() to tear down the tap and get the captured samples.
 *
 * onLimitReached fires once if the safety ceiling is hit so the caller can
 * stop playback and finalize the file.
 */
export async function createMp3Recorder({ onLimitReached } = {}) {
  const toneContext = Tone.getContext();
  const ctx = toneContext.rawContext;
  const sampleRate = ctx.sampleRate;
  const maxSamples = MAX_RECORDING_SECONDS * sampleRate;

  await ensureWorkletModule();
  const capture = toneContext.createAudioWorkletNode(WORKLET_NAME, {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [2],
    channelCount: 2,
    channelCountMode: 'explicit',
    channelInterpretation: 'speakers'
  });

  const leftChunks = [];
  const rightChunks = [];
  let totalSamples = 0;
  let recording = true;
  let limitNotified = false;

  capture.port.onmessage = (event) => {
    if (!recording) return;

    const { left, right } = event.data;
    for (let i = 0; i < left.length; i++) {
      leftChunks.push(left[i]);
      rightChunks.push(right[i]);
      totalSamples += left[i].length;
    }

    if (totalSamples >= maxSamples) {
      recording = false;
      if (!limitNotified) {
        limitNotified = true;
        if (onLimitReached) onLimitReached();
      }
    }
  };

  // Silent sink keeps the worklet in the active rendering graph without
  // adding audible output
  const sink = ctx.createGain();
  sink.gain.value = 0;

  // Tap both signal paths: Tone-based synths and soundfont instruments
  Tone.getDestination().connect(capture);
  getSoundfontBus().connect(capture);
  capture.connect(sink);
  sink.connect(ctx.destination);

  return {
    stop() {
      recording = false;
      capture.port.onmessage = null;
      try { Tone.getDestination().disconnect(capture); } catch (e) { /* already disconnected */ }
      try { getSoundfontBus().disconnect(capture); } catch (e) { /* already disconnected */ }
      try { capture.disconnect(); } catch (e) { /* noop */ }
      try { sink.disconnect(); } catch (e) { /* noop */ }
      return { leftChunks, rightChunks, sampleRate, totalSamples };
    }
  };
}

/**
 * Encodes captured PCM to an MP3 Blob with lamejs. Yields to the event loop
 * periodically so the UI stays responsive during encoding.
 */
export async function encodeMp3ToBlob({ leftChunks, rightChunks, sampleRate }, { kbps = 192 } = {}) {
  const lameModule = await import('@breezystack/lamejs');
  const lame = lameModule.default || lameModule;
  const encoder = new lame.Mp3Encoder(2, sampleRate, kbps);

  const mp3Chunks = [];
  let processedChunks = 0;

  for (let i = 0; i < leftChunks.length; i++) {
    const left = floatTo16BitPCM(leftChunks[i]);
    const right = floatTo16BitPCM(rightChunks[i]);
    const encoded = encoder.encodeBuffer(left, right);
    if (encoded.length > 0) {
      mp3Chunks.push(encoded);
    }

    // Yield roughly every second of audio to keep the UI responsive
    processedChunks++;
    if (processedChunks % 10 === 0) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  const finalChunk = encoder.flush();
  if (finalChunk.length > 0) {
    mp3Chunks.push(finalChunk);
  }

  return new Blob(mp3Chunks, { type: 'audio/mpeg' });
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Give the browser a moment to start the download before revoking
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function floatTo16BitPCM(float32Array) {
  const output = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const clamped = Math.max(-1, Math.min(1, float32Array[i]));
    output[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7FFF;
  }
  return output;
}
